import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { ScheduledApp } from '@crossnokaye/typescript-sdk/models/components';
import { ListScheduledAppsResponse } from '@crossnokaye/typescript-sdk/models/operations';

import { ScheduledAppKind } from '@/constants/scheduled-apps';
import { useAtlasClient } from '@/hooks/use-atlas-client';
import {
  listScheduledAppsQuery,
  ListScheduledAppsQueryProps,
} from '@/queries/scheduling/list-scheduled-apps';
import { AtlasQueryWithOrgIdAndAgentId } from '@/queries/types';

// Get all Scheduled Apps
export const useScheduledApps = ({
  orgId,
  agentId,
  enabled,
  kind,
  transform,
}: Omit<ListScheduledAppsQueryProps, 'client'>): UseQueryResult<ScheduledApp[] | undefined> => {
  const client = useAtlasClient();
  return useQuery(listScheduledAppsQuery({ orgId, agentId, enabled, kind, transform, client }));
};

// Find shutdown apps and sort them by enabled and version
const filterShutdownApp = (response: ListScheduledAppsResponse) => {
  return response.scheduledApps?.sort((a, b) => {
    // First sort by enabled (true comes first)
    if (a.status?.status.type !== b.status?.status.type) {
      // enabled is not disabled, so we want to sort enabled first
      return a.status?.status.type === 'Disabled' ? 1 : -1;
    }
    // Finally sort by version (higher version comes first)
    const aVersion = (a.package.version ?? '0.0.0').split('.').map(Number);
    const bVersion = (b.package.version ?? '0.0.0').split('.').map(Number);

    // Compare major version
    if (bVersion[0] !== aVersion[0]) return bVersion[0] - aVersion[0];
    // Compare minor version
    if (bVersion[1] !== aVersion[1]) return bVersion[1] - aVersion[1];
    // Compare patch version
    return bVersion[2] - aVersion[2];
  });
};
// Get Shutdown App(s)
export const useShutdownApp = ({
  orgId,
  agentId,
}: AtlasQueryWithOrgIdAndAgentId): UseQueryResult<ScheduledApp[] | undefined> => {
  const client = useAtlasClient();
  return useQuery(
    listScheduledAppsQuery({
      orgId,
      agentId,
      kind: ScheduledAppKind.BuildingShutdown,
      transform: filterShutdownApp,
      client,
    })
  );
};
